/**
 * `AuthService` — `/api/v1/auth/*`.
 *
 * Модули этого слоя тонкие и без состояния: путь, типы запроса и ответа,
 * разворот `response.data`. Ни ретраев, ни кэша, ни обработки ошибок —
 * первое живёт в `lib/http.ts`, второе и третье в сторах.
 *
 * Ошибки наружу выходят уже нормализованными в `ApiError` (response-интерцептор),
 * поэтому `try/catch` здесь был бы пустым слоем.
 */

import { AUTH_REFRESH_PATH, http } from '@/lib/http'
import type {
  ChangeMyPasswordRequest,
  ChangeMyPasswordResponse,
  GetMeResponse,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  LogoutResponse,
  RedeemDeviceLoginTicketRequest,
  RedeemDeviceLoginTicketResponse,
  RefreshRequest,
  RefreshResponse,
} from '@/types/api'

export const authApi = {
  /**
   * `POST /api/v1/auth/login` — публичный.
   * `login` принимает email **или** username одним полем.
   *
   * `skipAuth`: старого токена может не быть вовсе, а если он есть и протух —
   * 401 от логина обязан означать `INVALID_CREDENTIALS`, а не запускать ротацию.
   */
  async login(body: LoginRequest): Promise<LoginResponse> {
    const { data } = await http.post<LoginResponse>('/api/v1/auth/login', body, { skipAuth: true })
    return data
  },

  /**
   * `POST /api/v1/auth/refresh` — публичный.
   *
   * ⚠️ Приложению этот метод почти никогда не нужен: ротацией занимается
   * `refreshOnce()` из `lib/http.ts`, и звать надо **её** — только она
   * single-flight, а сервер ротирует токен на каждое использование.
   * Обёртка существует ради полноты контрактной поверхности и ручной отладки.
   */
  async refresh(body: RefreshRequest): Promise<RefreshResponse> {
    const { data } = await http.post<RefreshResponse>(AUTH_REFRESH_PATH, body, { skipAuth: true })
    return data
  },

  /**
   * `POST /api/v1/auth/logout` — публичный и идемпотентный.
   *
   * `skipAuth` здесь не «потому что можно», а потому что нужно: выход обязан
   * срабатывать и с протухшим access-токеном. Без флага 401 запустил бы
   * ротацию ради того, чтобы тут же погасить сессию, — а при мёртвом refresh
   * выход бы вовсе не состоялся.
   */
  async logout(body: LogoutRequest): Promise<LogoutResponse> {
    const { data } = await http.post<LogoutResponse>('/api/v1/auth/logout', body, { skipAuth: true })
    return data
  },

  /**
   * `POST /api/v1/auth/device-ticket` — обмен одноразового кода на сессию.
   * **Публичный.**
   *
   * `skipAuth` обязателен и здесь: у устройства оператора никакого токена ещё
   * нет, а если в этой вкладке лежит чужой протухший — 401 от него запустил бы
   * ротацию вместо входа.
   *
   * ⚠️ Любая неудача приезжает как `DEVICE_TICKET_INVALID` — сервер намеренно
   * не различает «нет кода», «уже погашен», «просрочен» и «это не код».
   * Не пытайтесь восстановить разницу: ровно её сокрытие и защищает эндпоинт
   * от перебора.
   */
  async redeemDeviceTicket(
    body: RedeemDeviceLoginTicketRequest,
  ): Promise<RedeemDeviceLoginTicketResponse> {
    const { data } = await http.post<RedeemDeviceLoginTicketResponse>(
      '/api/v1/auth/device-ticket',
      body,
      { skipAuth: true },
    )
    return data
  },

  /**
   * `GET /api/v1/auth/me`.
   *
   * ⚠️ Отдаёт **только** `user` — плоского списка прав в этом контракте нет
   * (поле зарезервировано под будущий RBAC). Аффордансы UI считаются из
   * `user.role` для системных экранов и из роли в составе мероприятия для
   * всего, что живёт внутри мероприятия.
   */
  async me(): Promise<GetMeResponse> {
    const { data } = await http.get<GetMeResponse>('/api/v1/auth/me')
    return data
  },

  /**
   * `POST /api/v1/auth/me/password` — смена **своего** пароля.
   *
   * В ответе — новая пара токенов (текущая сессия ротируется) и число
   * погашенных чужих сессий. Токены обязательно положить в хранилище:
   * старый access ещё жив, но его сессия уже другая.
   */
  async changeMyPassword(body: ChangeMyPasswordRequest): Promise<ChangeMyPasswordResponse> {
    const { data } = await http.post<ChangeMyPasswordResponse>('/api/v1/auth/me/password', body)
    return data
  },
}
